/**
 * 🔐 Auth Store (Zustand)
 *
 * State management للـ authentication.
 *
 * 🔒 Security Notes (C-9 fix):
 *   - ❌ لا نحفظ user object ولا isAuthenticated في localStorage
 *     (كان مهاجم يفتح DevTools ويكتب user.role='admin' → اختراق)
 *   - ✅ Firebase Auth SDK يحفظ الـ session في IndexedDB (browser-managed)
 *   - ✅ State يبدأ "غير مصادق" دائماً، حتى يرد onAuthStateChange بـ user حقيقي
 *   - ✅ الـ user.role يأتي من Firebase Custom Claims (موثوقة 100%)
 *
 *   النتيجة: حتى لو مهاجم زوّر localStorage، الـ state ما يتأثر،
 *   لأن الـ Firebase event هو السلطة الوحيدة.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type UserRole =
  | 'customer'
  | 'vendor'
  | 'admin'
  | 'super_admin'
  // 🆕 (Phase 2/3) Provider panel roles
  | 'provider_owner'
  | 'provider_manager'
  | 'booking_staff'
  | 'accountant'
  | 'viewer';

export interface AuthUser {
  readonly uid: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly displayName: string | null;
  readonly role: UserRole;
  readonly emailVerified: boolean;
  readonly photoURL?: string;
  /** 🆕 (Phase 4) للـ provider users: يجبر تغيير كلمة السر عند الدخول */
  readonly mustChangePassword?: boolean;
  /** 🆕 (Phase 4) providerId من Custom Claims (للـ provider users) */
  readonly providerId?: string;
  /** 🆕 (Phase 4) Permissions array من Firestore */
  readonly permissions?: readonly string[];
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean; // أثناء التحقق من session
  isHydrated: boolean;

  // Actions
  setUser: (user: AuthUser | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true, // يبدأ true حتى نتحقق من session
      isHydrated: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      logout: () => set({ user: null, isAuthenticated: false }),
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'havlaa-auth',
      storage: createJSONStorage(() => localStorage),
      // 🛡️ (C-9 fix) لا نحفظ user ولا isAuthenticated في localStorage
      //    Firebase Auth SDK يدير الـ session بنفسه (IndexedDB، آمن).
      //    عند reload، onAuthStateChange يطلق ويملأ الـ state من الـ token.
      partialize: () => ({}),
      onRehydrateStorage: () => (state) => {
        // ما عندنا شي للـ rehydrate، لكن نضع isHydrated=true
        // عشان الـ ProtectedRoute يكمّل (يعتمد بعدها على isLoading)
        state?.setHydrated();
      },
    }
  )
);

// ─── Selectors (لأداء أفضل) ───
export const useUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useIsAuthLoading = () => useAuthStore((s) => s.isLoading);

/**
 * 🛡️ هل الدور أدمن لوحة (admin أو super_admin)؟
 *
 * يُستخدم في حراسة الصلاحيات بدل `role === 'admin'` — فبعد أن صار
 * super_admin دوراً منفصلاً، الفحص المباشر على 'admin' كان يحرم
 * السوبر أدمن. السوبر أدمن يملك كل ما يملكه الأدمن وزيادة.
 */
export function isAdminRole(role: UserRole | undefined | null): boolean {
  return role === 'admin' || role === 'super_admin';
}
